import React, { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Formik, useFormikContext, getIn } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Button } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { FarmRiskType, FarmScheduleCoverageType, mineSubsidenceDefaulted } from '@ourbranch/lookups';
import { appConfig } from 'brand-config';

import { BrandFormField } from 'common/components/farm/brand-form-field';
import Field from 'core/components/form/form.v2';
import { currencyFormatter } from 'core/helpers/formatters';
import { calculateDeductibleAmount } from '../../helpers';
import { SubRiskCollapsibleSection } from './sub-risk-collapsible-section';
import FormSectionWrapper from 'common/components/form-section-wrapper';
import CollapsibleListItem from './collapsible-list-item';
import {
  ValidationErrorsModal,
  useValidationErrorsModal,
  setTouchedFromErrors
} from 'core/components/validation-errors-modal';

import { AppliedTag } from 'common/components/farm/applied-tag';
import useStyles from './sub-risk.styles';

const validationSchema = () =>
  Yup.object().shape({
    name: Yup.string().required('Structure name is required'),
    description: Yup.string().required('Description is required'),
    coverageMS: Yup.boolean(),
    hasActiveCoverageMS: Yup.boolean(),
    limit: Yup.number().required('Coverage limit is required'),
    deductibleAllOther: Yup.string().required('All peril deductible is required'),
    deductibleWindHail: Yup.string().required('Wind & Hail deductible is required')
  });

const RelatedPrivateStructuresForm = ({ fieldName, disabled = false, address, riskType, coverages }) => {
  const classes = useStyles();
  const { values, setFieldValue, setFieldTouched, validateForm, setErrors, setTouched } = useFormikContext();
  const { showValidationErrorsModal, setShowValidationErrorsModal } = useValidationErrorsModal();

  const { state, county } = address;

  // FO6 structures use Coverage E as the base for deductible calculations;
  // all other risk types (FO1/FO2/FO3) use Coverage A (dwelling limit)
  const baseCoverage = riskType === FarmRiskType.Fo6Structures ? coverages.coverageE : coverages.coverageA;
  const baseCoverageLabel = riskType === FarmRiskType.Fo6Structures ? 'Coverage E' : 'Coverage A';

  const isMineSubsidenceNonExemptCounty = mineSubsidenceDefaulted[state]?.[county?.toUpperCase()] ?? false;
  // Get Mine Subsidence coverage limit from appConfig (GitHub issue #28634)
  const mineSubsidenceCoverageLimit = appConfig.farm?.[state]?.coverageLimits?.mineSubsidenceCoverageLimit || 750000;

  const scheduleCoverages = getIn(values, fieldName) || [];
  const items =
    scheduleCoverages.find((item) => item.coverageType === FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES)
      ?.items || [];
  const coverageIndex = scheduleCoverages.findIndex(
    (c) => c.coverageType === FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES
  );

  // Auto-expand if there are any scheduled structure items
  const hasScheduledItems = items.length > 0;

  // All risk types use the same field names for deductibles
  const allPerilFieldName = 'deductibleAllOther';
  const windHailFieldName = 'deductibleWindHail';

  // Sync existing related private structure deductibles when parent risk deductibles change.
  // GitHub issue #29117: When a user updates Coverage A deductibles on the parent risk,
  // the deductibles on already-added related private structures must update to match.
  useEffect(() => {
    if (items.length === 0) {
      return;
    }

    const resolvedAllOther = calculateDeductibleAmount(coverages[allPerilFieldName], baseCoverage);
    const resolvedWindHail = calculateDeductibleAmount(coverages[windHailFieldName], baseCoverage);

    const needsUpdate = items.some(
      (item) =>
        item.extendedDetails?.deductibleAllOther !== resolvedAllOther ||
        item.extendedDetails?.deductibleWindHail !== resolvedWindHail
    );

    if (!needsUpdate) {
      return;
    }

    const updatedItems = items.map((item) => ({
      ...item,
      extendedDetails: {
        ...item.extendedDetails,
        deductibleAllOther: resolvedAllOther,
        deductibleWindHail: resolvedWindHail
      }
    }));

    const otherScheduleCoverages = scheduleCoverages.filter(
      (item) => item.coverageType !== FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES
    );

    setFieldValue(fieldName, [
      ...otherScheduleCoverages,
      {
        coverageType: FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES,
        items: updatedItems
      }
    ]);
    // items and scheduleCoverages are included to prevent stale closures when RPS items are
    // added/removed concurrently. The needsUpdate guard above ensures this effect never triggers
    // an infinite loop by short-circuiting when deductibles already match.
  }, [coverages[allPerilFieldName], coverages[windHailFieldName], baseCoverage, items, scheduleCoverages]);

  return (
    <>
      <ValidationErrorsModal open={showValidationErrorsModal} onClose={() => setShowValidationErrorsModal(false)} />
      <SubRiskCollapsibleSection
        type="secondary"
        content={<div className={classes.headerText}>Related private structures</div>}
        contentRight={hasScheduledItems ? <AppliedTag /> : null}
        initialOpen={hasScheduledItems}
      >
        <FormSectionWrapper
          sectionTitle="Scheduled items"
          totalLabel="structures"
          totalCount={items?.length || 0}
          emptyStateTitle="There are no structures added yet"
        >
          {/* list items */}
          {items.map((item, index) => {
            const structureName = item.extendedDetails?.name;
            const itemPath = `${fieldName}[${coverageIndex}].items[${index}]`;
            // Deductibles: Use calculateDeductibleAmount as a normalization layer to handle both
            // legacy data (percentage values like 0.01) and new data (dollar amounts).
            const allPerilDeductibleDisplay =
              item.extendedDetails?.deductibleAllOther && baseCoverage
                ? `$${calculateDeductibleAmount(item.extendedDetails.deductibleAllOther, baseCoverage).toLocaleString(
                    'en-US'
                  )}`
                : 'Not provided';
            const windHailDeductibleDisplay =
              item.extendedDetails?.deductibleWindHail && baseCoverage
                ? `$${calculateDeductibleAmount(item.extendedDetails.deductibleWindHail, baseCoverage).toLocaleString(
                    'en-US'
                  )}`
                : 'Not provided';

            return (
              <CollapsibleListItem
                key={`structure-${index}`}
                title={structureName}
                rightSideValues={[
                  { label: 'Mine Subsidence', value: item.extendedDetails?.coverageMS ? 'Included' : 'Waived' },
                  { label: 'Limit', value: `$${item.limit?.toLocaleString('en-US') || item.limit}` }
                ]}
                onDelete={() => {
                  const newItems = items.filter((_, checkIndex) => checkIndex !== index);
                  const currentScheduleCoverages =
                    getIn(values, fieldName)?.filter(
                      (item) => item.coverageType !== FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES
                    ) || [];
                  setFieldValue(`${fieldName}`, [
                    ...currentScheduleCoverages,
                    {
                      coverageType: FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES,
                      items: newItems
                    }
                  ]);
                  setFieldTouched(`${fieldName}.${FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES}`);
                }}
                disabled={disabled}
              >
                <Field
                  xs={6}
                  type="string"
                  id={`${itemPath}.extendedDetails.name`}
                  name={`${itemPath}.extendedDetails.name`}
                  mode="dark"
                  label="Structure Name"
                  disabled={disabled}
                />
                <Field
                  xs={6}
                  type="numeric"
                  id={`${itemPath}.limit`}
                  name={`${itemPath}.limit`}
                  mode="dark"
                  label="Coverage Limit"
                  format={{ thousandSeparator: true, prefix: '$' }}
                  disabled={disabled}
                />
                <Field
                  xs={12}
                  type="textarea"
                  id={`${itemPath}.extendedDetails.description`}
                  name={`${itemPath}.extendedDetails.description`}
                  mode="dark"
                  label="Description"
                  disabled={disabled}
                />
                <BrandFormField
                  xs={12}
                  type="select"
                  id={`${itemPath}.extendedDetails.coverageMS`}
                  name={`${itemPath}.extendedDetails.coverageMS`}
                  mode="dark"
                  coverages={{
                    ...coverages,
                    coverageMS: item.extendedDetails?.coverageMS,
                    hasActiveCoverageMS: item.extendedDetails?.hasActiveCoverageMS
                  }}
                  disabled={disabled || item.extendedDetails?.hasActiveCoverageMS}
                  state={address.state}
                  riskType={riskType}
                  county={address?.county}
                  targetCoverage={item.limit}
                  targetCoverageLimit={mineSubsidenceCoverageLimit}
                  helperTextLabel="Matches coverage limit"
                  fast={false}
                />
                <BrandFormField
                  xs={12}
                  type="checkbox"
                  id={`${itemPath}.extendedDetails.hasActiveCoverageMS`}
                  name={`${itemPath}.extendedDetails.hasActiveCoverageMS`}
                  mode="dark"
                  label="This structure has active mine subsidence or unrepaired mine damage"
                  disabled={disabled}
                  fast={false}
                  riskType={riskType}
                  state={state}
                />
                <Field
                  xs={6}
                  type="value"
                  id={`${itemPath}-deductibleAllOther-display`}
                  name={`${itemPath}-deductibleAllOther-display`}
                  mode="dark"
                  label="All peril deductible"
                  formatter={() => allPerilDeductibleDisplay}
                />
                <Field
                  xs={6}
                  type="value"
                  id={`${itemPath}-deductibleWindHail-display`}
                  name={`${itemPath}-deductibleWindHail-display`}
                  mode="dark"
                  label="Wind & Hail deductible"
                  formatter={() => windHailDeductibleDisplay}
                />
              </CollapsibleListItem>
            );
          })}

          {/* add form */}
          <div className={classes.addFormContainer}>
            <Formik
              initialValues={{
                name: undefined,
                description: undefined,
                coverageMS: isMineSubsidenceNonExemptCounty,
                hasActiveCoverageMS: false,
                limit: undefined,
                deductibleAllOther: coverages[allPerilFieldName],
                deductibleWindHail: coverages[windHailFieldName]
              }}
              validateOnChange={false}
              validateOnBlur={false}
              validationSchema={validationSchema}
              onSubmit={async (val, { resetForm }) => {
                const parentErrors = await validateForm();
                if (Object.keys(parentErrors).length > 0) {
                  setErrors(parentErrors);
                  setTouched(setTouchedFromErrors(parentErrors), false);
                  setShowValidationErrorsModal(true);
                  return;
                }

                const item = val;
                Object.keys(item).forEach((key) => {
                  if (item[key] === '') {
                    item[key] = null;
                  }
                });

                // Convert percentage-based deductibles to dollar amounts based on the risk's
                // base coverage (Coverage A for dwellings, Coverage E for FO6 structures)
                const deductibleAllOther = calculateDeductibleAmount(item.deductibleAllOther, baseCoverage);
                const deductibleWindHail = calculateDeductibleAmount(item.deductibleWindHail, baseCoverage);

                const { limit, name, description, coverageMS, hasActiveCoverageMS } = item;
                const newItem = {
                  limit,
                  extendedDetails: {
                    name,
                    description,
                    coverageMS,
                    hasActiveCoverageMS,
                    deductibleAllOther,
                    deductibleWindHail
                  }
                };

                const currentScheduleCoverages =
                  getIn(values, fieldName)?.filter(
                    (item) => item.coverageType !== FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES
                  ) || [];
                const currentRPSItems =
                  getIn(values, fieldName)?.find(
                    (item) => item.coverageType === FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES
                  )?.items || [];
                setFieldValue(`${fieldName}`, [
                  ...currentScheduleCoverages,
                  {
                    coverageType: FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES,
                    items: [...currentRPSItems, newItem]
                  }
                ]);
                setFieldTouched(`${fieldName}.${FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES}`);
                resetForm();
              }}
            >
              {({ submitForm, values }) => {
                // Memoize the coverages object to prevent unnecessary re-renders of BrandFormField
                // Only recreate when the specific mine subsidence values change
                const memoizedCoverages = useMemo(
                  () => ({
                    ...coverages,
                    coverageMS: values.coverageMS,
                    hasActiveCoverageMS: values.hasActiveCoverageMS
                  }),
                  [coverages, values.coverageMS, values.hasActiveCoverageMS]
                );

                // Memoize the disabled state to prevent unnecessary re-renders
                const mineSubsidenceDisabled = useMemo(
                  () => disabled || values.hasActiveCoverageMS,
                  [disabled, values.hasActiveCoverageMS]
                );

                return (
                  <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
                    <Field
                      xs={6}
                      type="string"
                      id="name"
                      name="name"
                      mode="dark"
                      label="Structure Name"
                      disabled={disabled}
                    />
                    <Field
                      xs={6}
                      type="numeric"
                      id="limit"
                      name="limit"
                      mode="dark"
                      label="Coverage Limit"
                      format={{ thousandSeparator: true, prefix: '$' }}
                      disabled={disabled}
                    />
                    <Field
                      xs={12}
                      type="textarea"
                      id="description"
                      name="description"
                      mode="dark"
                      label="Description of Structure"
                      disabled={disabled}
                    />
                    <BrandFormField
                      xs={12}
                      type="select"
                      id="coverageMS"
                      name="coverageMS"
                      mode="dark"
                      coverages={memoizedCoverages}
                      disabled={mineSubsidenceDisabled}
                      state={address.state}
                      riskType={riskType}
                      county={address?.county}
                      targetCoverage={values.limit}
                      targetCoverageLimit={mineSubsidenceCoverageLimit}
                      helperTextLabel="Matches coverage limit"
                    />
                    <BrandFormField
                      xs={12}
                      type="checkbox"
                      id="hasActiveCoverageMS"
                      name="hasActiveCoverageMS"
                      mode="dark"
                      label="This structure has active mine subsidence or unrepaired mine damage"
                      disabled={disabled}
                      riskType={riskType}
                      state={state}
                    />

                    <Field
                      xs={6}
                      type="value"
                      mode="dark"
                      fast={false}
                      label="All peril deductible"
                      value={
                        baseCoverage
                          ? currencyFormatter(calculateDeductibleAmount(coverages[allPerilFieldName], baseCoverage))
                          : `Enter ${baseCoverageLabel} first`
                      }
                    />

                    <Field
                      xs={6}
                      type="value"
                      mode="dark"
                      fast={false}
                      label="Wind & Hail deductible"
                      value={
                        baseCoverage
                          ? currencyFormatter(calculateDeductibleAmount(coverages[windHailFieldName], baseCoverage))
                          : `Enter ${baseCoverageLabel} first`
                      }
                    />

                    <Grid container alignItems="center" className={classes.singleBottom}>
                      <Button
                        type="button"
                        mode="big"
                        variant="contained"
                        color="secondary"
                        className={classes.button}
                        disabled={disabled}
                        onClick={submitForm}
                      >
                        Add Structure
                      </Button>
                    </Grid>
                  </Grid>
                );
              }}
            </Formik>
          </div>
        </FormSectionWrapper>
      </SubRiskCollapsibleSection>
    </>
  );
};

RelatedPrivateStructuresForm.propTypes = {
  fieldName: PropTypes.string.isRequired,
  disabled: PropTypes.bool
};

export default observer(RelatedPrivateStructuresForm);
